import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { useVenueMarket } from "@/hooks/useVenueMarket";
import { fmt } from "@/config/markets";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Sparkles } from "lucide-react";

export default function Billing() {
  const [venue, setVenue] = useState(null);
  const { market, symbol } = useVenueMarket();

  useEffect(() => {
    (async () => {
      const { data } = await http.get("/venues/me");
      setVenue(data);
    })();
  }, []);

  if (!venue) return <div className="p-10 text-sm text-zinc-500">Loading…</div>;

  const feePct = Math.round(market.bookingFeePercent * 100);
  const feeMinStr = fmt(market, market.bookingFeeMinimum, { decimals: 2 });
  const zeroStr = `${symbol}0`;

  return (
    <div className="p-8 lg:p-10 max-w-3xl" data-testid="billing-page">
      <div className="text-xs text-zinc-500 uppercase tracking-widest font-medium">Billing</div>
      <h1 className="mt-1 font-display font-black text-3xl lg:text-4xl tracking-tight">
        Keep more of every ticket you sell
      </h1>
      <p className="mt-2 text-zinc-500 max-w-2xl">
        {zeroStr} monthly fees. 100% of every ticket price lands in your Stripe. Your buyers pay a {feePct}% booking fee ({feeMinStr} minimum) at checkout — that&apos;s all we take, and we cover the card processing out of it.
      </p>

      {/* Free-plan hero */}
      <div className="mt-8 rounded-2xl bg-zinc-900 text-white p-8 relative overflow-hidden" data-testid="billing-plan-card">
        <div className="absolute inset-0 grid-bg opacity-[0.06]" aria-hidden="true" />
        <div className="relative">
          <div className="flex items-baseline gap-2">
            <span className="font-display font-black text-5xl tracking-tighter">{zeroStr}</span>
            <span className="text-zinc-400">/ month · free forever</span>
          </div>
          <div className="mt-2 text-sm text-zinc-400">
            No monthly fees. No per-ticket cost to you. We&apos;re paid entirely by the buyer&apos;s {feePct}% booking fee ({feeMinStr} minimum).
          </div>
          <div className="mt-4">
            <Badge className="bg-emerald-500"><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Active</Badge>
          </div>
          <ul className="mt-6 space-y-2 text-sm">
            {[
              "Unlimited events, tickets and buyers — no ticket price cap",
              `Buyer pays a ${feePct}% booking fee (${feeMinStr} minimum) — you keep 100% of every ticket`,
              "We absorb Stripe card processing on every sale out of that fee",
              "Direct Stripe payouts — money never touches us",
              "Fully white-label event pages, custom subdomain & email",
              "Digital QR tickets, scanner and membership cards",
            ].map((t) => (
              <li key={t} className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                {t}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Reassurance strip */}
      <div className="mt-6 rounded-xl bg-emerald-50 border border-emerald-200 p-5 flex items-start gap-3">
        <span className="w-9 h-9 shrink-0 grid place-items-center rounded-lg bg-emerald-600 text-white">
          <Sparkles className="w-4 h-4" />
        </span>
        <div>
          <div className="font-display font-bold text-emerald-900">Nothing to pay. Nothing to renew.</div>
          <p className="mt-1 text-sm text-emerald-800 max-w-2xl">
            Your success is our success. If you never sell a ticket, you never owe us a penny. If you sell out every event, your bill from us stays exactly the same: <strong>{zeroStr}</strong>.
          </p>
        </div>
      </div>
    </div>
  );
}
